import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useMotionValueEvent, useTransform, type MotionValue } from 'motion/react';
import { ArrowRight, MessageSquare, Layers, Music, AudioLines } from 'lucide-react';
import { UseCaseBriefOverlay } from './ProjectBriefDrawer';

/* ──────────────────────────────────────────────────────────────
   Use Cases — homepage section between "The Voqals Advantage"
   and "Custom Data Collection".

   Two render trees:
   - Desktop: simple static 4-column grid that fits one viewport
     (the homepage hijacks the wheel into one-section-per-scroll,
     so a sticky-scroll effect would fight it). Cards just fade
     up via useInView on enter.
   - Mobile: sticky-scroll matched to the USP-section UX. Outer is
     ~280dvh; the inner sticky stage pins the heading + paragraph
     at the top while the use-case card below crossfades from
     1 → 2 → 3 → 4 as the user scrolls through the section. Same
     scroll-progress pattern Usp1Mobile uses (manual listener on
     #main-scroll-container).
   ────────────────────────────────────────────────────────────── */

/* — Mini animated SVG decorations (top-right of each card) — */
const ChatBarsVisual: React.FC<{ accent: string; active: boolean }> = ({ accent, active }) => {
  const bars = [0.45, 0.85, 0.6, 0.95, 0.4, 0.7, 0.85, 0.55, 0.9, 0.5, 0.75];
  return (
    <svg viewBox="0 0 120 36" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      {bars.map((h, i) => {
        const x = 4 + i * 11;
        const barH = h * 28;
        return (
          <motion.rect
            key={i}
            x={x}
            y={18 - barH / 2}
            width="4"
            height={barH}
            rx="2"
            fill={accent}
            fillOpacity="0.85"
            initial={{ scaleY: 0.3 }}
            animate={active ? { scaleY: [0.3, 1, 0.45, 1, 0.3] } : { scaleY: 0.3 }}
            style={{ transformOrigin: `${x + 2}px 18px` }}
            transition={{
              duration: 2.6,
              repeat: active ? Infinity : 0,
              delay: i * 0.08,
              ease: 'easeInOut',
            }}
          />
        );
      })}
    </svg>
  );
};

const TwinWavesVisual: React.FC<{ accent: string; active: boolean }> = ({ accent, active }) => {
  const wave = [0.45, 0.65, 0.5, 0.7, 0.55, 0.6, 0.5, 0.65, 0.55, 0.6, 0.5];
  return (
    <svg viewBox="0 0 120 36" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      {[0, 1].map((row) => (
        <g key={row} transform={`translate(0, ${row === 0 ? 4 : 22})`}>
          {wave.map((h, i) => {
            const x = 4 + i * 11;
            const barH = h * 12;
            return (
              <motion.rect
                key={i}
                x={x}
                y={6 - barH / 2}
                width="4"
                height={barH}
                rx="2"
                fill={accent}
                fillOpacity={row === 0 ? '0.85' : '0.55'}
                initial={{ scaleY: 0.4 }}
                animate={active ? { scaleY: [0.4, 1, 0.4] } : { scaleY: 0.4 }}
                style={{ transformOrigin: `${x + 2}px 6px` }}
                transition={{
                  duration: 2.4,
                  repeat: active ? Infinity : 0,
                  delay: i * 0.07 + row * 0.3,
                  ease: 'easeInOut',
                }}
              />
            );
          })}
        </g>
      ))}
    </svg>
  );
};

const StemsVisual: React.FC<{ accent: string; active: boolean }> = ({ accent, active }) => {
  const tracks = [0.85, 0.65, 0.45, 0.75];
  const beats = [1, 0, 1, 1, 0, 1, 1, 0, 1, 1, 0];
  return (
    <svg viewBox="0 0 120 36" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      {tracks.map((amp, row) => {
        const rowY = 4 + row * 8;
        return (
          <g key={row}>
            {beats.map((b, i) => {
              if (!b) return null;
              const x = 4 + i * 11;
              const barH = amp * 6;
              return (
                <motion.rect
                  key={i}
                  x={x}
                  y={rowY - barH / 2 + 4}
                  width="4"
                  height={barH}
                  rx="1.5"
                  fill={accent}
                  fillOpacity={0.5 + amp * 0.4}
                  initial={{ scaleY: 0.3 }}
                  animate={active ? { scaleY: [0.3, 1, 0.3] } : { scaleY: 0.3 }}
                  style={{ transformOrigin: `${x + 2}px ${rowY + 4}px` }}
                  transition={{
                    duration: 1.8,
                    repeat: active ? Infinity : 0,
                    delay: i * 0.06 + row * 0.15,
                    ease: 'easeInOut',
                  }}
                />
              );
            })}
          </g>
        );
      })}
    </svg>
  );
};

const SpectrogramVisual: React.FC<{ accent: string; active: boolean }> = ({ accent, active }) => {
  const cells = [
    [0.3, 0.7, 0.4, 0.85, 0.5, 0.95, 0.6, 0.8, 0.4, 0.7, 0.55],
    [0.55, 0.8, 0.65, 0.45, 0.85, 0.5, 0.4, 0.95, 0.6, 0.45, 0.7],
    [0.4, 0.5, 0.85, 0.55, 0.4, 0.7, 0.85, 0.45, 0.55, 0.85, 0.5],
    [0.75, 0.4, 0.55, 0.7, 0.85, 0.4, 0.6, 0.5, 0.85, 0.55, 0.7],
  ];
  return (
    <svg viewBox="0 0 120 36" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      {cells.map((row, r) =>
        row.map((intensity, c) => {
          const x = 4 + c * 11;
          const y = 4 + r * 7;
          return (
            <motion.rect
              key={`${r}-${c}`}
              x={x}
              y={y}
              width="8"
              height="5"
              rx="1"
              fill={accent}
              initial={{ fillOpacity: 0.1 }}
              animate={active ? { fillOpacity: [0.1, intensity, 0.1] } : { fillOpacity: 0.1 }}
              transition={{
                duration: 2.4,
                repeat: active ? Infinity : 0,
                delay: c * 0.05 + r * 0.1,
                ease: 'easeInOut',
              }}
            />
          );
        })
      )}
    </svg>
  );
};

type UseCase = {
  id: string;
  title: string;
  tagline: string;
  examples: string[];
  accent: string;
  Icon: React.ComponentType<{ className?: string }>;
  Visual: React.ComponentType<{ accent: string; active: boolean }>;
};

const USE_CASES: UseCase[] = [
  {
    id: 'conversational-ai',
    title: 'Conversational AI',
    tagline: 'Voice agents that talk like real Indians — code-switched, region-aware, on-brand.',
    examples: ['Voice Agents', 'IVR Bots', 'Multilingual UI'],
    accent: '#56CFFC',
    Icon: MessageSquare,
    Visual: ChatBarsVisual,
  },
  {
    id: 'voice-cloning',
    title: 'Voice Cloning',
    tagline: 'Hours-deep voice capture across full register and emotional range.',
    examples: ['TTS Engines', 'Branded Voices', 'Dubbing'],
    accent: '#A07BFF',
    Icon: Layers,
    Visual: TwinWavesVisual,
  },
  {
    id: 'generative-music',
    title: 'Generative Music',
    tagline: 'Multi-track stems at every recording phase — Indian, global, every genre.',
    examples: ['Music Models', 'Stem-Aware AI', 'Composer Tools'],
    accent: '#5EE7C7',
    Icon: Music,
    Visual: StemsVisual,
  },
  {
    id: 'speech-recognition',
    title: 'Speech Recognition',
    tagline: 'Real-world Indian speech — code-switched, multi-speaker, fully annotated.',
    examples: ['ASR Engines', 'STT Pipelines', 'NLU'],
    accent: '#7B9CFF',
    Icon: AudioLines,
    Visual: SpectrogramVisual,
  },
];

/* — Card body shared between mobile and desktop renders — */
const UseCaseCardBody: React.FC<{ useCase: UseCase; active: boolean; index: number }> = ({ useCase, active, index }) => {
  const { Icon, Visual, accent } = useCase;
  return (
    <>
      {/* Accent gradient blob in the corner */}
      <div
        className="absolute -top-[20%] -right-[15%] w-[60%] h-[60%] rounded-full pointer-events-none blur-[80px] transition-opacity duration-500 opacity-55 group-hover:opacity-100"
        style={{ background: `${accent}40` }}
      />
      {/* Hover-only border + halo boost */}
      <div
        className="absolute inset-0 rounded-[inherit] pointer-events-none transition-opacity duration-500 opacity-0 group-hover:opacity-100"
        style={{ boxShadow: `0 0 0 1px ${accent}99, 0 36px 96px ${accent}33` }}
      />
      {/* Mini animated visual — top-right corner, accent-coloured */}
      <div className="absolute top-[clamp(14px,1.1vw,20px)] right-[clamp(14px,1.1vw,20px)] w-[clamp(70px,6vw,96px)] h-[clamp(26px,2.4vw,36px)] z-10 opacity-90 transition-opacity duration-500 group-hover:opacity-100">
        <Visual accent={accent} active={active} />
      </div>

      <div className="relative z-10 flex flex-col h-full gap-[clamp(12px,0.85vw,16px)]">
        <motion.div
          className="inline-flex items-center justify-center rounded-xl shrink-0"
          style={{
            width: 'clamp(44px, 2.8vw, 48px)',
            height: 'clamp(44px, 2.8vw, 48px)',
            background: `linear-gradient(135deg, ${accent}30, ${accent}14)`,
            border: `1px solid ${accent}66`,
          }}
          animate={active ? { boxShadow: [`0 0 16px ${accent}26`, `0 0 28px ${accent}55`, `0 0 16px ${accent}26`] } : {}}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut', delay: index * 0.4 }}
        >
          <Icon className="w-[clamp(18px,1.3vw,22px)] h-[clamp(18px,1.3vw,22px)]" style={{ color: accent }} />
        </motion.div>
        <h3
          // md:min-h reserves 2 lines so cards whose title is 1 line still
          // leave the same vertical space as the longest one ("Speech
          // Recognition") that wraps to 2 lines on desktop.
          className="font-display font-black text-[clamp(20px,1.7vw,28px)] tracking-tight leading-[1.05] uppercase text-transparent bg-clip-text md:min-h-[clamp(46px,3.6vw,60px)]"
          style={{ backgroundImage: `linear-gradient(90deg, #ffffff 0%, #E0F4FF 60%, ${accent} 100%)` }}
        >
          {useCase.title}
        </h3>
        {/* min-h reserves 3 lines worth of vertical space regardless of how
            many lines this card's tagline actually wraps to — keeps the chips
            and CTA below it at the same Y position across all 4 cards. */}
        <p className="text-white/80 text-[clamp(13px,0.85vw,14px)] leading-snug italic line-clamp-3 min-h-[clamp(54px,4.6vw,60px)]">
          {useCase.tagline}
        </p>
        {/* Chips. On desktop the card column is narrow (~222 px content
            width) and at the original 10-11px text + 8-12px horizontal
            padding the chips wrapped to 2 rows for 3 of the 4 cards but
            stayed on 1 row for the 4th — uneven heights. Shrinking the
            desktop chip text/padding makes all 4 cards fit on 1 row, which
            also keeps the chip block height uniform across cards. Mobile
            chips keep the original sizing (the full-width mobile card has
            plenty of room). */}
        <div className="flex flex-wrap gap-[clamp(4px,0.4vw,8px)] md:gap-[clamp(2px,0.18vw,5px)]">
          {useCase.examples.map((ex) => (
            <span
              key={ex}
              className="text-[10px] md:text-[clamp(9px,0.62vw,11px)] font-semibold tracking-tight px-[clamp(8px,0.7vw,12px)] md:px-[clamp(3px,0.25vw,8px)] py-[clamp(3px,0.3vw,5px)] md:py-[clamp(2px,0.2vw,4px)] rounded-full whitespace-nowrap"
              style={{
                color: 'rgba(255,255,255,0.85)',
                background: `linear-gradient(135deg, ${accent}1f, ${accent}08)`,
                border: `1px solid ${accent}40`,
              }}
            >
              {ex}
            </span>
          ))}
        </div>
        <div
          className="flex items-center gap-1.5 text-[clamp(10px,0.62vw,11px)] font-bold tracking-[0.22em] uppercase transition-colors"
          style={{ color: accent }}
        >
          <span>Brief Us</span>
          <ArrowRight className="w-[clamp(12px,0.85vw,14px)] h-[clamp(12px,0.85vw,14px)] transition-transform duration-500 group-hover:translate-x-1" />
        </div>
      </div>
    </>
  );
};

const cardClassName = (accent: string) => ({
  className: 'group relative block h-full w-full text-left rounded-[clamp(18px,1.4vw,24px)] overflow-hidden p-[clamp(20px,1.6vw,28px)] transition-all duration-500 hover:-translate-y-1.5 cursor-pointer',
  style: {
    background: `linear-gradient(160deg, ${accent}14, rgba(8,8,16,0.88) 55%, rgba(5,5,10,0.94))`,
    border: `1px solid ${accent}40`,
    boxShadow: `0 18px 48px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)`,
  } as React.CSSProperties,
});

/* — Desktop card: simple useInView fade-up reveal as the section enters the viewport. — */
const DesktopCard: React.FC<{ useCase: UseCase; index: number; onSelect: (uc: UseCase) => void }> = ({ useCase, index, onSelect }) => {
  const ref = useRef<HTMLButtonElement>(null);
  const inView = useInView(ref, { amount: 0.3, margin: '0px 0px -10% 0px', once: true });
  const { className, style } = cardClassName(useCase.accent);
  return (
    <motion.button
      ref={ref}
      type="button"
      onClick={() => onSelect(useCase)}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 32 }}
      transition={{ duration: 0.7, delay: 0.1 + index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className={className}
      style={style}
    >
      <UseCaseCardBody useCase={useCase} active={inView} index={index} />
    </motion.button>
  );
};

/* — Mobile sticky-scroll swap card: cards stack absolutely in the same slot;
   each crossfades in over its slice of scroll progress and out as the next
   one takes over. Card 0 visible at p=0, card 3 fully visible by p=1. — */
const SwapCard: React.FC<{
  useCase: UseCase;
  index: number;
  total: number;
  progress: MotionValue<number>;
  onSelect: (uc: UseCase) => void;
}> = ({ useCase, index, total, progress, onSelect }) => {
  const slice = 1 / total;
  const center = (index + 0.5) * slice;
  // 35% of the slice on either side of center is "fully visible"; outside that
  // we crossfade with the neighbour. First card is already at peak at p=0;
  // last card holds peak through p=1 so neither cuts off at the section bounds.
  const peakStart = index === 0 ? 0 : center - slice * 0.35;
  const peakEnd = index === total - 1 ? 1 : center + slice * 0.35;
  const fadeIn = index === 0 ? 0 : peakStart - slice * 0.3;
  const fadeOut = index === total - 1 ? 1 : peakEnd + slice * 0.3;

  // Build keyframes — when index === 0, fadeIn === peakStart so we collapse
  // them to avoid duplicate-x errors in useTransform.
  const stops = index === 0
    ? [peakEnd, fadeOut]
    : index === total - 1
    ? [fadeIn, peakStart]
    : [fadeIn, peakStart, peakEnd, fadeOut];
  const values = index === 0
    ? [1, 0]
    : index === total - 1
    ? [0, 1]
    : [0, 1, 1, 0];

  const opacity = useTransform(progress, stops, values);

  // Card is "active" (animations on visuals) when it's in its peak window.
  const [active, setActive] = useState(index === 0);
  useMotionValueEvent(progress, 'change', (v: number) => {
    setActive(v >= peakStart - 0.02 && v <= peakEnd + 0.02);
  });

  const { className, style } = cardClassName(useCase.accent);
  return (
    <motion.button
      type="button"
      onClick={() => onSelect(useCase)}
      // Position absolute (top/left/right only — let height be content-driven so
      // there's no empty void inside the card) and override `relative` from
      // cardClassName via inline style.
      style={{ opacity, ...style, position: 'absolute', top: 0, left: 0, right: 0 }}
      className={className}
    >
      <UseCaseCardBody useCase={useCase} active={active} index={index} />
    </motion.button>
  );
};

/* — Progress dot — lights up when its card slice is active. — */
const ProgressDot: React.FC<{ index: number; total: number; progress: MotionValue<number>; accent: string }> = ({ index, total, progress, accent }) => {
  const slice = 1 / total;
  const start = index * slice;
  const end = (index + 1) * slice;
  const [active, setActive] = useState(index === 0);
  useMotionValueEvent(progress, 'change', (v: number) => {
    // Last dot stays active when v reaches exactly 1 (avoids dropping off).
    setActive(v >= start && (index === total - 1 ? v <= end + 0.001 : v < end));
  });
  return (
    <span
      className="h-[3px] rounded-full transition-all duration-500"
      style={{
        width: active ? 28 : 14,
        background: active ? accent : 'rgba(255,255,255,0.2)',
        boxShadow: active ? `0 0 10px ${accent}88` : 'none',
      }}
    />
  );
};

/* — Section header (heading + sub) shared between mobile and desktop. Font
   sizes and inter-element spacing match the rest of the homepage's section
   headers (var(--heading-usp), var(--text-body), mb-6 between pill+heading
   and heading+paragraph). */
const SectionHeader: React.FC = () => (
  <div className="relative z-10 max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] flex flex-col items-center text-center">
    <div className="inline-flex items-center gap-2 px-5 py-2 md:gap-[clamp(4px,0.46vw,8px)] md:px-[clamp(16px,1.39vw,24px)] md:py-[clamp(6px,0.52vw,9px)] rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)] mb-6 md:mb-[clamp(10px,0.85vw,16px)]">
      Use Cases
    </div>
    <h2 className="font-display text-[length:var(--heading-usp)] md:text-[clamp(40px,4.2vw,76px)] font-black tracking-tighter leading-[1.05] text-[#EEEEEE] uppercase mb-6 md:mb-[clamp(12px,1.1vw,20px)]">
      ANY VOICE AI.<br />
      <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic inline-block pb-1 pr-2">
        EVERY USE CASE.
      </span>
    </h2>
    <p className="text-white/85 text-[length:var(--text-body)] md:text-[clamp(13px,0.95vw,17px)] leading-relaxed text-center max-w-sm md:max-w-[clamp(540px,55vw,820px)] mx-auto">
      Conversational agents, voice cloning, generative music, speech recognition — every voice-AI use case has its own data needs. Voqals builds for all of them: studio-certified Indian speech across every register, every dialect, ready to drop into your training pipeline.
    </p>
  </div>
);

const BackgroundGlows: React.FC = () => (
  <>
    <div className="absolute top-1/4 left-[10%] w-[42%] h-[42%] bg-[#56CFFC]/[0.08] blur-[160px] rounded-full pointer-events-none" />
    <div className="absolute bottom-1/4 right-[10%] w-[42%] h-[42%] bg-[#A07BFF]/[0.08] blur-[150px] rounded-full pointer-events-none" />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[40%] bg-[#5EE7C7]/[0.05] blur-[170px] rounded-full pointer-events-none" />
  </>
);

/* — Mobile sticky-scroll wrapper: pins heading + paragraph at the top of the
   viewport while the use-case card below crossfades through the four cards
   as the user scrolls. Mirrors the Usp1Mobile / Usp2Mobile pattern.
   Horizontal swipes on the card slot also advance through cards — same
   underlying scroll just nudged by one slice in either direction. — */
const UseCasesMobile: React.FC<{ onSelect: (uc: UseCase) => void }> = ({ onSelect }) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollYProgress = useMotionValue(0);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  // The card index a swipe is targeting (or last settled at via vertical
  // scroll). Each swipe increments/decrements this — so a rapid double-swipe
  // advances exactly two cards, not 3+ from reading mid-animation scrollTop.
  const targetIdxRef = useRef(0);
  // True between when a swipe fires a smooth-scrollTo and when that scroll
  // resolves; while true, the scroll listener doesn't overwrite targetIdxRef
  // (preventing the in-flight scroll from leaking into the swipe target).
  const animatingRef = useRef(false);
  const lastSwipeAt = useRef(0);

  // Helper: walk offsetParent chain to get section's absolute position within
  // the scroll container. Shared between the scroll listener and the swipe
  // handler so both compute progress the same way.
  const getSectionMetrics = () => {
    const container = document.getElementById('main-scroll-container');
    const section = sectionRef.current;
    if (!container || !section) return null;
    let sectionTop = 0;
    let el: HTMLElement | null = section;
    while (el && el !== container) {
      sectionTop += el.offsetTop;
      el = el.offsetParent as HTMLElement | null;
    }
    const sectionHeight = section.offsetHeight;
    const containerHeight = container.clientHeight;
    const runwayEnd = sectionTop + sectionHeight - containerHeight;
    return { container, sectionTop, runwayEnd, runway: runwayEnd - sectionTop };
  };

  useEffect(() => {
    const metrics = getSectionMetrics();
    if (!metrics) return;
    const { container } = metrics;
    const handleScroll = () => {
      const m = getSectionMetrics();
      if (!m) return;
      const progress = m.runway > 0
        ? Math.max(0, Math.min(1, (m.container.scrollTop - m.sectionTop) / m.runway))
        : 0;
      scrollYProgress.set(progress);
      // Only let vertical scroll resync targetIdxRef when no swipe-driven
      // smooth-scroll is in flight — otherwise the in-flight scrollTop would
      // overwrite our intended target.
      if (!animatingRef.current) {
        targetIdxRef.current = Math.min(
          USE_CASES.length - 1,
          Math.max(0, Math.round(progress * (USE_CASES.length - 1))),
        );
      }
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, [scrollYProgress]);

  // Horizontal swipe → step one card in that direction. Each swipe nudges
  // `targetIdxRef` by exactly ±1; a rapid double-swipe advances by 2 cards
  // (one per swipe), never more. Throttled so a single fast finger gesture
  // can't be misread as multiple swipes.
  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    // Require clear horizontal intent: ≥ 40px and ≥ 1.4× more horizontal than
    // vertical (so vertical scrolls aren't hijacked).
    if (absDx < 40 || absDx < absDy * 1.4) return;
    // Throttle: one swipe per 350 ms, so a single fast flick can't register
    // as multiple gestures.
    const now = performance.now();
    if (now - lastSwipeAt.current < 350) return;
    lastSwipeAt.current = now;

    const metrics = getSectionMetrics();
    if (!metrics) return;
    const { container, sectionTop, runwayEnd, runway } = metrics;
    if (runway <= 0) return;
    const slice = runway / USE_CASES.length;
    const direction = dx < 0 ? 1 : -1; // swipe left → next, swipe right → prev
    const nextIdx = Math.max(0, Math.min(USE_CASES.length - 1, targetIdxRef.current + direction));
    if (nextIdx === targetIdxRef.current) return; // at boundary
    targetIdxRef.current = nextIdx;
    // Land at the centre of the target slice (peak visibility of that card).
    const targetScroll = Math.max(sectionTop, Math.min(runwayEnd, sectionTop + (nextIdx + 0.5) * slice));
    animatingRef.current = true;
    container.scrollTo({ top: targetScroll, behavior: 'smooth' });
    // Re-enable scroll-listener-driven resync once the smooth scroll has had
    // time to settle. ~450 ms is comfortably above browser smooth-scroll dur.
    window.setTimeout(() => { animatingRef.current = false; }, 450);
  };

  return (
    <div ref={sectionRef} className="md:hidden h-[280dvh] w-full relative shrink-0">
      <div className="sticky top-0 h-[100dvh] w-full flex flex-col overflow-hidden pt-[clamp(108px,26vw,148px)] pb-[clamp(36px,9vw,56px)]">
        <BackgroundGlows />
        {/* Pinned heading + paragraph — natural-flow at top */}
        <div className="relative z-10 shrink-0">
          <SectionHeader />
        </div>
        {/* Card slot — sits 24px below the paragraph (matches the
            heading→paragraph rhythm). Fixed height so all four cards land
            at exactly the same dimensions; combined with the tagline's
            min-h, every element inside lines up at the same Y across cards.
            Horizontal swipes on this slot step through cards (in addition
            to vertical page-scroll, which drives the same crossfade). */}
        <div
          className="relative z-10 shrink-0 mt-6 max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)]"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="relative w-full h-[clamp(252px,68vw,288px)]">
            {USE_CASES.map((uc, i) => (
              <SwapCard
                key={uc.id}
                useCase={uc}
                index={i}
                total={USE_CASES.length}
                progress={scrollYProgress}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
        {/* Progress dots — sit 24px below the card with a clear gap. */}
        <div className="relative z-10 shrink-0 mt-6 flex items-center justify-center gap-2">
          {USE_CASES.map((uc, i) => (
            <ProgressDot
              key={uc.id}
              index={i}
              total={USE_CASES.length}
              progress={scrollYProgress}
              accent={uc.accent}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const UseCasesSection: React.FC = () => {
  const [openUseCase, setOpenUseCase] = useState<UseCase | null>(null);

  return (
    <section id="use-cases" className="w-full shrink-0 relative scroll-mt-[clamp(90px,7.64vw,132px)]">
      {/* MOBILE — sticky-scroll: heading + paragraph stay pinned at top, card
         below crossfades through the 4 use cases as the user scrolls. */}
      <UseCasesMobile onSelect={setOpenUseCase} />

      {/* DESKTOP — single-viewport static 4-column grid. Plays nicely with the
         homepage's wheel-hijack scroll system (one section per scroll-tick). */}
      <div className="hidden md:block relative w-full pt-[clamp(80px,7vw,128px)] pb-[clamp(64px,5vw,96px)] overflow-hidden">
        <BackgroundGlows />
        <SectionHeader />
        <div className="relative z-10 max-w-[var(--max-content)] w-full mx-auto px-[var(--px-container)] mt-[clamp(36px,3.4vw,64px)]">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-[clamp(12px,1vw,20px)]">
            {USE_CASES.map((uc, i) => (
              <DesktopCard key={uc.id} useCase={uc} index={i} onSelect={setOpenUseCase} />
            ))}
          </div>
        </div>
      </div>

      <UseCaseBriefOverlay
        isOpen={openUseCase !== null}
        onClose={() => setOpenUseCase(null)}
        useCase={openUseCase?.title ?? ''}
      />
    </section>
  );
};

export default UseCasesSection;
